import { InputNode, InputTargetNode, WaitQueueNode } from '@internal/open-catalog';
import { Button, createStyles, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, makeStyles, TextField, Theme } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';

export interface InputTargetDialogProps {
  open: boolean;
  source: InputNode;
  onClose: (newTarget?: InputTargetNode) => void;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      paddingTop: 0,
      paddingBottom: 0
    },
    infoList: {
      '& > li': {
        padding: 0
      },
      '& > li > div': {
        maxWidth: '50%',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1)
      },
      '& > li > div:first-child': {
        paddingLeft: 0
      },
      '& > li > div:last-child': {
        paddingRight: 0
      }
    },
    target: {
      overflow: 'unset',
      '& div[class|="MuiFormControl-root"]': {
        width: '100%'
      }
    }
  })
);

export function InputTargetDialog(props: InputTargetDialogProps) {

  const classes = useStyles();
  const { onClose, open, source } = props;

  const [target, setTarget] = useState<string>();
  const inputRef = useRef<HTMLInputElement>();

  const reservedNames = ['stream', 'delayed', 'wait', 'parking-lot', 'debug'];
  const isReserved = (t?: string) => reservedNames.includes(t?.trim().toLowerCase() || '');

  const emptyState = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.value = '';
    }
    setTarget('');
  };

  const cancel = () => {
    onClose();
    emptyState();
  };

  const submit = () => {

    const name = `${source.name}.${target}`;

    const queue: InputTargetNode = {
      name,
      type: 'quorum',
      bindings: [],
      arguments: {
        'x-dead-letter-exchange': '',
        'x-dead-letter-routing-key': `${name}.wait`,
        'x-queue-type': 'quorum'
      },
      wait: {
        name: `${name}.wait`,
        type: 'quorum',
        arguments: {
          'x-dead-letter-exchange': '',
          'x-dead-letter-routing-key': name,
          'x-queue-type': 'quorum',
          'x-message-ttl': 1000
        }
      } as WaitQueueNode
    } as InputTargetNode;

    onClose(queue);
    emptyState();
  };

  const handleDestinationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTarget(event.target.value);
  };

  const onPressEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (target) {
        submit();
      }
    }
  }

  useEffect(() => {
    setTimeout(() => inputRef?.current?.focus(), 100)
  }, [open, inputRef]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>New target queue</DialogTitle>
      <DialogContent className={classes.content}>
        <List dense className={classes.infoList}>
          <ListItem>
            <ListItemText primary="Source" secondary={source.name} title={source.name} />
            <ListItemText className={classes.target}>
              <TextField required
                inputRef={inputRef} 
                label="Destination"
                variant="outlined"
                size="small"
                onKeyUp={onPressEnter}
                onChange={handleDestinationChange}
                helperText={isReserved(target) ? `Warning: '${target || ''}' is a reserved word` : undefined}
              />
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText />
            <ListItemText secondary={`${source.name}.${target}`} title={`${source.name}.${target}`} />
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} color="primary">Cancel</Button>
        <Button onClick={submit} color="secondary" disabled={!target?.length || isReserved(target)}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
};